/**
 * Registry of fictional customer accounts shared by the sim (tenant datasets), the server seed,
 * template previews and tests. Names are invented; never the screenshot sample names.
 */
import { z } from 'zod';
import type { AccountId, AccountProvider } from '../ids.js';
import { uuidFromSeed, isoDaysAgo } from './prng.js';

export const SIZE_TIERS = ['S', 'M', 'L'] as const;
export const SizeTierSchema = z.enum(SIZE_TIERS);
export type SizeTier = z.infer<typeof SizeTierSchema>;

// ---------------------------------------------------------------------------------------------
// Frameworks
// ---------------------------------------------------------------------------------------------

export const FRAMEWORK_IDS = ['soc2', 'iso27001', 'hipaa', 'pcidss', 'nistcsf'] as const;
export type FrameworkFixtureId = (typeof FRAMEWORK_IDS)[number];

export interface FrameworkFixture {
  id: FrameworkFixtureId;
  name: string;
  /** Vanta framework id as returned by /v1/frameworks. */
  vantaId: string;
  /** Drata framework slug. */
  drataSlug: string;
  /** Control code prefixes used for framework-control externalIds. */
  controlCodes: readonly string[];
  description: string;
}

export const FRAMEWORKS: Record<FrameworkFixtureId, FrameworkFixture> = {
  soc2: {
    id: 'soc2',
    name: 'SOC 2',
    vantaId: 'soc2',
    drataSlug: 'soc-2',
    controlCodes: ['CC1.1', 'CC1.2', 'CC2.1', 'CC3.1', 'CC3.2', 'CC4.1', 'CC5.1', 'CC5.2', 'CC6.1', 'CC6.2', 'CC6.3', 'CC6.6', 'CC6.7', 'CC6.8', 'CC7.1', 'CC7.2', 'CC7.3', 'CC7.4', 'CC8.1', 'CC9.1', 'CC9.2', 'A1.1', 'A1.2', 'C1.1', 'PI1.1'],
    description: 'AICPA Trust Services Criteria for security, availability and confidentiality.',
  },
  iso27001: {
    id: 'iso27001',
    name: 'ISO 27001',
    vantaId: 'iso27001-2022',
    drataSlug: 'iso-27001',
    controlCodes: ['A.5.1', 'A.5.9', 'A.5.15', 'A.5.17', 'A.5.19', 'A.5.23', 'A.5.24', 'A.5.29', 'A.5.30', 'A.6.1', 'A.6.3', 'A.6.5', 'A.7.4', 'A.8.1', 'A.8.2', 'A.8.5', 'A.8.7', 'A.8.8', 'A.8.9', 'A.8.13', 'A.8.15', 'A.8.16', 'A.8.24', 'A.8.28', 'A.8.32'],
    description: 'ISO/IEC 27001:2022 information security management system controls (Annex A).',
  },
  hipaa: {
    id: 'hipaa',
    name: 'HIPAA',
    vantaId: 'hipaa',
    drataSlug: 'hipaa',
    controlCodes: ['164.308(a)(1)', '164.308(a)(3)', '164.308(a)(4)', '164.308(a)(5)', '164.308(a)(6)', '164.308(a)(7)', '164.308(b)', '164.310(a)', '164.310(d)', '164.312(a)(1)', '164.312(a)(2)', '164.312(b)', '164.312(c)', '164.312(d)', '164.312(e)', '164.314(a)', '164.316(b)'],
    description: 'HIPAA Security Rule administrative, physical and technical safeguards.',
  },
  pcidss: {
    id: 'pcidss',
    name: 'PCI DSS',
    vantaId: 'pci-dss-4',
    drataSlug: 'pci-dss',
    controlCodes: ['1.2', '1.3', '2.2', '3.4', '3.5', '4.2', '5.2', '6.3', '6.4', '7.2', '8.3', '8.4', '8.6', '10.2', '10.4', '11.3', '11.4', '12.3', '12.6', '12.8'],
    description: 'PCI DSS v4.0 requirements for protecting cardholder data.',
  },
  nistcsf: {
    id: 'nistcsf',
    name: 'NIST CSF',
    vantaId: 'nist-csf-2',
    drataSlug: 'nist-csf',
    controlCodes: ['GV.OC-01', 'GV.RM-01', 'ID.AM-01', 'ID.AM-02', 'ID.RA-01', 'ID.RA-05', 'PR.AA-01', 'PR.AA-03', 'PR.AA-05', 'PR.AT-01', 'PR.DS-01', 'PR.DS-02', 'PR.DS-11', 'PR.PS-01', 'PR.PS-02', 'DE.CM-01', 'DE.CM-09', 'DE.AE-02', 'RS.MA-01', 'RS.AN-03', 'RC.RP-01'],
    description: 'NIST Cybersecurity Framework 2.0 functions: Govern, Identify, Protect, Detect, Respond, Recover.',
  },
};

// ---------------------------------------------------------------------------------------------
// Chaos settings (sim control panel; defaults are all off)
// ---------------------------------------------------------------------------------------------

export const ChaosSettingsSchema = z.object({
  /** Return 429 (with Retry-After) on every Nth request; null disables. */
  rateLimitEveryN: z.number().int().positive().nullable(),
  /** Dataset id whose fetch fails (500) at `failPageOrdinal`; null disables. */
  failDataset: z.string().nullable(),
  /** 1-based page ordinal that fails for `failDataset`; null means the final page. */
  failPageOrdinal: z.number().int().positive().nullable(),
  /** Expire the bearer token after N successful requests (forces 401 then renewal). */
  expireTokenAfterN: z.number().int().positive().nullable(),
  /** Added latency per response in milliseconds. */
  slowMs: z.number().int().nonnegative(),
  /** Dataset id that returns a malformed page once; null disables. */
  malformedPage: z.string().nullable(),
  /** Drata: personnel endpoint answers 403 (missing scope) instead of data. */
  drataMissingPersonnelScope: z.boolean(),
  /** Return the same cursor twice for one dataset (repeated-cursor detection). */
  duplicateCursor: z.boolean(),
});
export type ChaosSettings = z.infer<typeof ChaosSettingsSchema>;

export const CHAOS_DEFAULTS: ChaosSettings = {
  rateLimitEveryN: null,
  failDataset: null,
  failPageOrdinal: null,
  expireTokenAfterN: null,
  slowMs: 0,
  malformedPage: null,
  drataMissingPersonnelScope: false,
  duplicateCursor: false,
};

// ---------------------------------------------------------------------------------------------
// Customers
// ---------------------------------------------------------------------------------------------

/** Named scenarios a tenant is built to exercise (tests look these up by tag). */
export const SCENARIO_TAGS = [
  'a10_device_intersection',
  'duplicate_people_names',
  'large_dataset',
  'drata_two_workspaces',
  'drata_missing_personnel_fields',
  'hipaa_training',
  'pci_scope',
  'template_only',
] as const;
export type ScenarioTag = (typeof SCENARIO_TAGS)[number];

export interface VantaTenantCredentials {
  clientId: string;
  clientSecret: string;
  /** Provider tenant identifier (Vanta organisation id). */
  tenantId: string;
  /** Browser URL for deep links; stored separately from the API base URL (spec section 5). */
  browserUrl: string;
  organizationName: string;
}

export interface DrataWorkspaceFixture {
  id: string;
  name: string;
  slug: string;
  isDefault: boolean;
}

export interface DrataTenantCredentials {
  apiKey: string;
  /** Provider tenant identifier (Drata account id). */
  tenantId: string;
  workspaces: readonly DrataWorkspaceFixture[];
  browserUrl: string;
}

export interface CustomerFixture {
  slug: string;
  accountId: AccountId;
  displayName: string;
  provider: AccountProvider;
  tier: SizeTier;
  industry: string;
  /** Domain used for synthetic employee emails. */
  emailDomain: string;
  /** Internal account note (never customer findings). */
  notes: string;
  frameworks: readonly FrameworkFixtureId[];
  vanta: VantaTenantCredentials | null;
  drata: DrataTenantCredentials | null;
  chaos: ChaosSettings;
  /** Staff persona slugs assigned to this account (assignments in the seed). */
  assignedStaffSlugs: readonly string[];
  scenarios: readonly ScenarioTag[];
  createdAt: string;
}

interface CustomerInput {
  slug: string;
  displayName: string;
  provider: AccountProvider;
  tier: SizeTier;
  industry: string;
  notes: string;
  frameworks: readonly FrameworkFixtureId[];
  assignedStaffSlugs: readonly string[];
  scenarios?: readonly ScenarioTag[];
  drataWorkspaces?: readonly { name: string; slug: string }[];
  createdDaysAgo: number;
}

function customer(input: CustomerInput): CustomerFixture {
  const accountId = uuidFromSeed(`account:${input.slug}`) as AccountId;
  const vanta: VantaTenantCredentials | null =
    input.provider === 'vanta'
      ? {
          clientId: `vanta-${input.slug}`,
          clientSecret: `secret-${input.slug}`,
          tenantId: `org_${uuidFromSeed(`vanta-tenant:${input.slug}`).replace(/-/g, '').slice(0, 20)}`,
          browserUrl: `https://app.vanta.com/${input.slug}`,
          organizationName: input.displayName,
        }
      : null;
  const drata: DrataTenantCredentials | null =
    input.provider === 'drata'
      ? {
          apiKey: `drata-${input.slug}`,
          tenantId: `acct_${uuidFromSeed(`drata-tenant:${input.slug}`).replace(/-/g, '').slice(0, 16)}`,
          browserUrl: `https://app.drata.com/${input.slug}`,
          workspaces: (input.drataWorkspaces ?? [{ name: input.displayName, slug: input.slug }]).map((w, i) => ({
            id: String(4000 + parseInt(uuidFromSeed(`drata-workspace:${input.slug}:${w.slug}`).slice(0, 4), 16)),
            name: w.name,
            slug: w.slug,
            isDefault: i === 0,
          })),
        }
      : null;
  return {
    slug: input.slug,
    accountId,
    displayName: input.displayName,
    provider: input.provider,
    tier: input.tier,
    industry: input.industry,
    emailDomain: `${input.slug}.example`,
    notes: input.notes,
    frameworks: input.frameworks,
    vanta,
    drata,
    chaos: { ...CHAOS_DEFAULTS },
    assignedStaffSlugs: input.assignedStaffSlugs,
    scenarios: [...(input.scenarios ?? []), ...(input.provider === 'template_only' ? (['template_only'] as const) : [])],
    createdAt: isoDaysAgo(input.createdDaysAgo),
  };
}

export const CUSTOMERS: readonly CustomerFixture[] = [
  customer({
    slug: 'halcyon-robotics',
    displayName: 'Halcyon Robotics',
    provider: 'vanta',
    tier: 'M',
    industry: 'Industrial robotics',
    notes: 'Weekly Tuesday sync. Device fleet managed through Jamf; screen-lock remediation in progress.',
    frameworks: ['soc2', 'iso27001'],
    assignedStaffSlugs: ['amir-tarighat', 'priya-raman', 'reed-castellano'],
    scenarios: ['a10_device_intersection', 'duplicate_people_names'],
    createdDaysAgo: 110,
  }),
  customer({
    slug: 'meridian-health-partners',
    displayName: 'Meridian Health Partners',
    provider: 'vanta',
    tier: 'L',
    industry: 'Healthcare services',
    notes: 'Large tenant. HIPAA training required for all clinical staff. Expect long syncs at the 50/min ceiling.',
    frameworks: ['hipaa', 'soc2'],
    assignedStaffSlugs: ['amir-tarighat', 'tomas-herrera'],
    scenarios: ['large_dataset', 'hipaa_training'],
    createdDaysAgo: 100,
  }),
  customer({
    slug: 'ferrostat-energy',
    displayName: 'Ferrostat Energy',
    provider: 'vanta',
    tier: 'S',
    industry: 'Energy storage',
    notes: 'NIST CSF programme mapped onto ISO 27001. Small team; single AWS account.',
    frameworks: ['nistcsf', 'iso27001'],
    assignedStaffSlugs: ['amir-tarighat', 'priya-raman', 'tomas-herrera'],
    createdDaysAgo: 85,
  }),
  customer({
    slug: 'bluewater-logistics',
    displayName: 'Bluewater Logistics',
    provider: 'vanta',
    tier: 'S',
    industry: 'Freight and logistics',
    notes: 'First SOC 2 Type I. Onboarding new operator.',
    frameworks: ['soc2'],
    assignedStaffSlugs: ['amir-tarighat', 'priya-raman', 'noor-haddad'],
    createdDaysAgo: 40,
  }),
  customer({
    slug: 'kestrel-fintech',
    displayName: 'Kestrel Fintech',
    provider: 'vanta',
    tier: 'M',
    industry: 'Payments',
    notes: 'PCI DSS scope limited to the card vault VPC. Quarterly ASV scans due at month end.',
    frameworks: ['soc2', 'pcidss', 'iso27001'],
    assignedStaffSlugs: ['amir-tarighat', 'tomas-herrera'],
    scenarios: ['pci_scope', 'duplicate_people_names'],
    createdDaysAgo: 75,
  }),
  customer({
    slug: 'orchard-biotech',
    displayName: 'Orchard Biotech',
    provider: 'drata',
    tier: 'M',
    industry: 'Biotechnology',
    notes: 'Two Drata workspaces: corporate and clinical systems. Confirm workspace before sync.',
    frameworks: ['soc2', 'hipaa'],
    assignedStaffSlugs: ['amir-tarighat', 'priya-raman'],
    scenarios: ['drata_two_workspaces', 'hipaa_training'],
    drataWorkspaces: [
      { name: 'Orchard Biotech Corporate', slug: 'orchard-corporate' },
      { name: 'Orchard Clinical Systems', slug: 'orchard-clinical' },
    ],
    createdDaysAgo: 70,
  }),
  customer({
    slug: 'solstice-media',
    displayName: 'Solstice Media',
    provider: 'drata',
    tier: 'S',
    industry: 'Digital media',
    notes: 'Personnel data incomplete in Drata (HR integration pending). Expect UNKNOWN checks.',
    frameworks: ['soc2'],
    assignedStaffSlugs: ['amir-tarighat', 'tomas-herrera', 'reed-castellano'],
    scenarios: ['drata_missing_personnel_fields'],
    createdDaysAgo: 55,
  }),
  customer({
    slug: 'granite-peak-insurance',
    displayName: 'Granite Peak Insurance',
    provider: 'drata',
    tier: 'L',
    industry: 'Insurance',
    notes: 'Large tenant with PCI scope. Shared office egress with two other Drata customers.',
    frameworks: ['soc2', 'iso27001', 'pcidss'],
    assignedStaffSlugs: ['amir-tarighat', 'priya-raman'],
    scenarios: ['large_dataset', 'pci_scope'],
    createdDaysAgo: 95,
  }),
  customer({
    slug: 'tidewater-analytics',
    displayName: 'Tidewater Analytics',
    provider: 'template_only',
    tier: 'S',
    industry: 'Data analytics',
    notes: 'Advisory engagement. No provider connection; agendas and notes only.',
    frameworks: ['soc2'],
    assignedStaffSlugs: ['amir-tarighat', 'priya-raman'],
    createdDaysAgo: 30,
  }),
  customer({
    slug: 'verdant-foods',
    displayName: 'Verdant Foods',
    provider: 'template_only',
    tier: 'S',
    industry: 'Food distribution',
    notes: 'Pre-sales readiness review. Provider selection pending.',
    frameworks: ['iso27001'],
    assignedStaffSlugs: ['amir-tarighat', 'tomas-herrera'],
    createdDaysAgo: 12,
  }),
];

export const VANTA_CUSTOMERS: readonly CustomerFixture[] = CUSTOMERS.filter((c) => c.provider === 'vanta');
export const DRATA_CUSTOMERS: readonly CustomerFixture[] = CUSTOMERS.filter((c) => c.provider === 'drata');
export const TEMPLATE_ONLY_CUSTOMERS: readonly CustomerFixture[] = CUSTOMERS.filter((c) => c.provider === 'template_only');

/** Look up by slug or account id (case-insensitive). */
export function findCustomer(slugOrAccountId: string): CustomerFixture | undefined {
  const needle = slugOrAccountId.toLowerCase();
  return CUSTOMERS.find((c) => c.slug === needle || c.accountId.toLowerCase() === needle);
}

export function customerBySlug(slug: string): CustomerFixture {
  const found = findCustomer(slug);
  if (!found) throw new Error(`unknown customer fixture: ${slug}`);
  return found;
}

export function customersWithScenario(tag: ScenarioTag): CustomerFixture[] {
  return CUSTOMERS.filter((c) => c.scenarios.includes(tag));
}

/** Sim helper: resolve a Vanta client-credentials pair to its tenant. */
export function customerForVantaCredentials(clientId: string, clientSecret: string): CustomerFixture | undefined {
  return VANTA_CUSTOMERS.find((c) => c.vanta?.clientId === clientId && c.vanta.clientSecret === clientSecret);
}

/** Sim helper: resolve a Drata API key to its tenant. */
export function customerForDrataApiKey(apiKey: string): CustomerFixture | undefined {
  return DRATA_CUSTOMERS.find((c) => c.drata?.apiKey === apiKey);
}

/** Customers a staff persona is assigned to. */
export function customersForStaff(slug: string): CustomerFixture[] {
  return CUSTOMERS.filter((c) => c.assignedStaffSlugs.includes(slug));
}

/** Names that must never appear in fixtures (screenshot sample data, spec section 22). */
export const FORBIDDEN_SAMPLE_NAMES: readonly string[] = ['Northwind Aerospace'];
